// PRO Portfolio Drift & Rebalancing Alpha Auditor -- TypeScript contracts
// mirroring backend/app/domain/portfolio_drift_auditor_pro/schemas.py.

export type RegimeState = 'BULL_MOMENTUM' | 'HIGH_VOL_CAUTION' | 'DRAWDOWN_SHIELD';
export type TradeAction = 'BUY' | 'SELL' | 'HOLD';

export interface ProHoldingInput {
    ticker: string;
    target_weight: number; // fraction 0..1
    current_weight?: number | null; // optional live drifted weight (fraction)
}

export interface ProDriftAuditRequest {
    holdings: ProHoldingInput[];
    initial_capital: number;
    lookback_years: number;
    corridor_threshold: number; // fraction, e.g. 0.05
    transaction_cost_bps: number;
    capital_gains_tax_bps: number;
    monthly_cash_inflow: number;
    rebalance_cooldown_days: number;
    risk_free_rate: number; // fraction, e.g. 0.04
}

export interface ProStrategyMetricsRow {
    strategy_id: string;
    strategy_name: string;
    is_locked_pro_tier: boolean;
    final_wealth: number;
    total_contributions: number;
    cagr_pct: number;
    annualized_volatility_pct: number;
    max_drawdown_pct: number;
    ulcer_index: number;
    sharpe_ratio: number;
    sortino_ratio: number;
    calmar_ratio: number;
    cvar_95_pct: number;
    cvar_99_pct: number;
    variance_drain_annual_pct: number;
    variance_drain_total_dollars: number;
    rebalance_events_count: number;
    annual_turnover_pct: number;
    total_fee_drag_dollars: number;
    total_tax_drag_dollars: number;
}

export interface ProHoldingDriftDiagnostic {
    ticker: string;
    target_weight_pct: number;
    final_drifted_weight_pct: number;
    ai_regime_optimal_weight_pct: number;
    absolute_drift_pct: number;
    max_historical_weight_pct: number;
    risk_contribution_target_pct: number;
    risk_contribution_drifted_pct: number;
    risk_contribution_ai_pro_pct: number;
    trailing_6m_momentum_pct: number;
    trailing_6m_downside_vol_pct: number;
}

export interface CorridorFrontierPoint {
    corridor_threshold_pct: number;
    cagr_pct: number;
    sharpe_ratio: number;
    max_drawdown_pct: number;
    rebalance_count: number;
    annual_turnover_pct: number;
    total_friction_cost_dollars: number;
    is_optimal_sharpe: boolean;
}

export interface RebalanceTradeEvent {
    event_index: number;
    date: string;
    regime_state: RegimeState;
    trigger_ticker: string;
    max_relative_drift_pct: number;
    portfolio_wealth_after_trade: number;
    turnover_pct: number;
    fee_and_tax_cost_dollars: number;
    weights_after_trade: Record<string, number>;
}

export interface ActionableTradeOrder {
    ticker: string;
    action: TradeAction;
    current_weight_pct: number;
    static_target_weight_pct: number;
    ai_regime_target_weight_pct: number;
    current_dollars: number;
    ai_target_dollars: number;
    trade_dollars: number;
    estimated_fee_dollars: number;
    risk_contribution_before_pct: number;
    risk_contribution_after_pct: number;
}

export interface ProTimeSeriesPoint {
    date: string;
    drifted_wealth: number;
    calendar_wealth: number;
    corridor_wealth: number;
    cashflow_corridor_wealth: number;
    ai_pro_wealth: number;
    drifted_drawdown_pct: number;
    corridor_drawdown_pct: number;
    ai_pro_drawdown_pct: number;
    regime_state_code: number; // 0 Bull, 1 High-Vol, 2 Drawdown Shield
    drifted_weights: Record<string, number>;
    ai_pro_weights: Record<string, number>;
}

export interface ProExecutiveSummaryBanner {
    headline: string;
    subheadline: string;
    // Live macro regime name (dominant ensemble conviction, e.g. "Slowdown")
    // read from the macro_regime domain -- the single source of truth shared
    // with the Macro Compass. Null on a fresh deploy before macro data exists.
    macro_regime_name?: string | null;
    optimal_corridor_threshold_pct: number;
    active_regime_state: RegimeState;
    total_inefficiency_cost_dollars: number;
    net_ai_alpha_vs_drift_dollars: number;
    net_ai_alpha_vs_best_static_dollars: number;
    cagr_edge_vs_drift_pct: number;
    variance_drain_saved_dollars: number;
    total_friction_saved_by_ai_dollars: number;
    max_single_asset_bloat_ticker: string;
    max_single_asset_bloat_pct: number;
}

export interface ProDriftAuditResponse {
    status: string;
    missing_tickers_queued: string[];
    start_date: string;
    end_date: string;
    trading_days: number;
    executive_banner: ProExecutiveSummaryBanner;
    strategy_comparison: ProStrategyMetricsRow[];
    corridor_frontier: CorridorFrontierPoint[];
    holding_diagnostics: ProHoldingDriftDiagnostic[];
    live_execution_blotter: ActionableTradeOrder[];
    trade_ledger: RebalanceTradeEvent[];
    timeseries: ProTimeSeriesPoint[];
}

// Price-history coverage for a set of tickers (POST /coverage), used to build
// the data-aware lookback dropdown so the UI never offers more history than
// the local store actually holds.
export interface CoverageResponse {
    start_date: string | null;
    end_date: string | null;
    trading_days: number;
    max_lookback_years: number;
    available_lookback_options: number[];
    missing_tickers: string[];
}

// Local form row (UI-only; percentages, not fractions).
export interface HoldingRow {
    id: number;
    ticker: string;
    weight: number; // percent (target)
    currentWeight: number | null; // percent (optional live drifted)
}

// Shared regime metadata for consistent labelling + colour across the UI.
export const REGIME_META: Record<
    RegimeState,
    { label: string; short: string; color: string; bg: string; text: string; code: number }
> = {
    BULL_MOMENTUM: {
        label: 'Bull Momentum',
        short: 'Bull',
        color: '#10b981',
        bg: 'bg-emerald-100 dark:bg-emerald-500/15',
        text: 'text-emerald-700 dark:text-emerald-300',
        code: 0,
    },
    HIGH_VOL_CAUTION: {
        label: 'High-Vol Caution',
        short: 'Caution',
        color: '#f59e0b',
        bg: 'bg-amber-100 dark:bg-amber-500/15',
        text: 'text-amber-700 dark:text-amber-300',
        code: 1,
    },
    DRAWDOWN_SHIELD: {
        label: 'Drawdown Shield',
        short: 'Shield',
        color: '#f43f5e',
        bg: 'bg-rose-100 dark:bg-rose-500/15',
        text: 'text-rose-700 dark:text-rose-300',
        code: 2,
    },
};

export const REGIME_BY_CODE: Record<number, RegimeState> = {
    0: 'BULL_MOMENTUM',
    1: 'HIGH_VOL_CAUTION',
    2: 'DRAWDOWN_SHIELD',
};
